import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Beef, Wheat, Droplets, CheckCircle, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function NutritionDeepDiveGuide() {
  const navigate = useNavigate();

  const macros = [
    {
      name: "Protein",
      icon: Beef,
      color: "rose",
      role: "Building blocks for muscles, hormones, enzymes, and immune cells",
      dailyNeed: "0.8-1g per pound of body weight",
      sources: ["Chicken & turkey", "Fish & seafood", "Eggs", "Greek yogurt", "Legumes & lentils", "Tofu & tempeh"],
      tip: "Include protein at every meal to maintain muscle mass, especially important after 40"
    },
    {
      name: "Carbohydrates",
      icon: Wheat,
      color: "amber",
      role: "Primary energy source for brain and body",
      dailyNeed: "45-65% of daily calories",
      sources: ["Whole grains (quinoa, oats)", "Sweet potatoes", "Fruits", "Vegetables", "Beans & legumes", "Brown rice"],
      tip: "Choose complex carbs over simple ones for sustained energy and stable blood sugar"
    },
    {
      name: "Fats",
      icon: Droplets,
      color: "emerald",
      role: "Hormone production, brain health, nutrient absorption",
      dailyNeed: "20-35% of daily calories",
      sources: ["Avocados", "Olive oil", "Nuts & seeds", "Fatty fish (salmon)", "Nut butters", "Dark chocolate"],
      tip: "Don't fear healthy fats! They're essential for hormone balance in your 40s"
    }
  ];

  const myths = [
    { myth: "Carbs make you fat", truth: "Excess calories make you gain weight, not carbs. Quality and quantity matter." },
    { myth: "Fat is bad for you", truth: "Healthy fats are essential. Trans fats and excessive saturated fats are the real culprits." },
    { myth: "You need supplements", truth: "A balanced diet usually provides everything you need. Test before supplementing." },
    { myth: "Eating late causes weight gain", truth: "Total daily calories matter more than timing. But late eating can disrupt sleep." }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
            Week 2 • Day 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Nutrition Deep Dive
          </h1>
          <p className="text-xl text-secondary-600">Understanding Macronutrients for Optimal Health</p>
        </div>

        {/* Introduction */}
        <section className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <BookOpen className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Learning</h3>
              <p className="text-indigo-100 text-lg">
                Spend 20 minutes learning about macronutrients. Understanding the basics empowers you to 
                make informed choices without falling for diet trends.
              </p>
            </div>
          </div>
        </section>

        {/* Macronutrients */}
        <section className="space-y-6 mb-8">
          <h2 className="text-2xl font-serif font-bold text-secondary-900">The Three Macronutrients</h2>
          
          {macros.map((macro, index) => (
            <div key={index} className={`bg-white rounded-2xl p-6 shadow-md border border-gray-100`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full bg-${macro.color}-100 flex items-center justify-center`}>
                  <macro.icon className={`w-6 h-6 text-${macro.color}-600`} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-secondary-900">{macro.name}</h3>
                  <p className="text-sm text-secondary-500">Daily need: {macro.dailyNeed}</p>
                </div>
              </div>
              
              <p className="text-secondary-700 mb-4">{macro.role}</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-secondary-500 mb-2">Best Sources:</p>
                  <ul className="space-y-1">
                    {macro.sources.map((source, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                        <CheckCircle className={`w-4 h-4 text-${macro.color}-500 flex-shrink-0`} />
                        {source}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`bg-${macro.color}-50 rounded-xl p-4`}>
                  <p className="text-sm text-secondary-700 flex items-start gap-2">
                    <Lightbulb className={`w-4 h-4 text-${macro.color}-500 mt-0.5 flex-shrink-0`} />
                    <span><strong>Pro Tip:</strong> {macro.tip}</span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Nutrition Myths */}
        <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-indigo-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Common Nutrition Myths</h2>
          <div className="space-y-4">
            {myths.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <p className="text-red-600 font-medium line-through mb-1">Myth: {item.myth}</p>
                <p className="text-green-700"><strong>Truth:</strong> {item.truth}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Action Step */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Your Action Step</h2>
          <p className="text-secondary-700 mb-4">
            Choose one macronutrient to focus on this week. Identify 3 healthy sources you'll incorporate 
            into your meals. Write them down and add them to your grocery list.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {["Protein", "Carbs", "Fats"].map((macro, i) => (
              <div key={i} className="p-4 bg-indigo-50 rounded-xl text-center">
                <p className="font-bold text-indigo-700 mb-2">{macro} Focus</p>
                <p className="text-sm text-secondary-600">3 sources I'll add this week:</p>
                <div className="mt-2 space-y-1">
                  <div className="h-6 border-b border-indigo-200"></div>
                  <div className="h-6 border-b border-indigo-200"></div>
                  <div className="h-6 border-b border-indigo-200"></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default NutritionDeepDiveGuide;
